import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ImageOff, MoreHorizontal, Wand2, FileText, Send, BadgeCheck, ArrowRight, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { StatusPill } from "@/components/StatusPill";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { SIMPLE_STATUS_META, AVAILABILITY_META, fmtEUR, fmtKm, fmtDateTime } from "@/lib/constants";

const SIMPLE_FILTERS = ["entwurf", "fotos", "pruefung", "bereit", "online", "verkauft"];

export default function VehicleListPage() {
  const [vehicles, setVehicles] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("alle");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = ["owner", "manager"].includes(user?.role);
  const { t } = useLang();

  useEffect(() => {
    const timer = setTimeout(() => {
      api
        .get("/vehicles", { params: { search: search || undefined } })
        .then((r) => setVehicles(r.data))
        .catch(() => setVehicles([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const filtered = useMemo(() => {
    if (!vehicles) return null;
    if (statusFilter === "alle") return vehicles;
    return vehicles.filter((v) => v.simple_status === statusFilter);
  }, [vehicles, statusFilter]);

  const QuickActions = ({ v }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`vehicle-actions-${v.bestandsnummer}`}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => navigate(`/app/fahrzeuge/${v.id}`)} data-testid={`action-open-${v.bestandsnummer}`}>
          <ArrowRight className="h-3.5 w-3.5 mr-2" /> Fortsetzen
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/app/fotobooth?vehicle=${v.id}`)} data-testid={`action-fotobooth-${v.bestandsnummer}`}>
          <Wand2 className="h-3.5 w-3.5 mr-2" /> Fotobooth öffnen
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/app/fahrzeuge/${v.id}?tab=description`)} data-testid={`action-listing-${v.bestandsnummer}`}>
          <FileText className="h-3.5 w-3.5 mr-2" /> Inserat bearbeiten
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/app/fahrzeuge/${v.id}?tab=publish`)} data-testid={`action-publish-${v.bestandsnummer}`}>
          <Send className="h-3.5 w-3.5 mr-2" /> Veröffentlichen
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/app/fahrzeuge/${v.id}?sold=1`)} data-testid={`action-sold-${v.bestandsnummer}`}>
          <BadgeCheck className="h-3.5 w-3.5 mr-2" /> Als verkauft markieren
        </DropdownMenuItem>
        {canDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setDeleteTarget(v)}
              className="text-red-400 focus:text-red-400"
              data-testid={`action-delete-${v.bestandsnummer}`}
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Fahrzeug löschen
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/vehicles/${deleteTarget.id}`);
      setVehicles((list) => (list || []).filter((x) => x.id !== deleteTarget.id));
      toast.success(`Fahrzeug ${deleteTarget.marke || ""} ${deleteTarget.modell || ""} (${deleteTarget.bestandsnummer}) gelöscht`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(apiError(e, "Löschen fehlgeschlagen"));
    } finally { setDeleteBusy(false); }
  };

  return (
    <div className="space-y-4 pb-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold tracking-[-0.02em]">{t("vehicles.title")}</h1>
        <Button onClick={() => navigate("/app/aufnehmen")} className="gap-2 font-semibold" data-testid="vehicle-list-capture-button">
          <Plus className="h-4 w-4" /> {t("vehicles.new")}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input
          placeholder="Suche: Marke, Modell, FIN, Bestandsnr."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          data-testid="vehicle-search-input"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[190px]" data-testid="vehicle-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Status</SelectItem>
            {SIMPLE_FILTERS.map((k) => (
              <SelectItem key={k} value={k}>{SIMPLE_STATUS_META[k]?.label || k}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!filtered ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : filtered.length === 0 ? (
        <Card className="py-14 text-center" data-testid="vehicle-list-empty">
          <p className="text-sm text-muted-foreground">Keine Fahrzeuge gefunden.</p>
          <Button variant="outline" className="mt-3 gap-2" onClick={() => navigate("/app/aufnehmen")}>
            <Plus className="h-4 w-4" /> Erstes Fahrzeug hinzufügen
          </Button>
        </Card>
      ) : (
        <>
          {/* Desktop-Tabelle */}
          <Card className="hidden md:block overflow-hidden">
            <table className="w-full text-sm" data-testid="vehicle-table">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2.5 font-medium">Fahrzeug</th>
                  <th className="px-3 py-2.5 font-medium">KM</th>
                  <th className="px-3 py-2.5 font-medium">Preis</th>
                  <th className="px-3 py-2.5 font-medium">Verfügbarkeit</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-3 py-2.5 font-medium">Online</th>
                  <th className="px-3 py-2.5 font-medium">Letzte Änderung</th>
                  <th className="px-2 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => {
                  const meta = SIMPLE_STATUS_META[v.simple_status] || SIMPLE_STATUS_META.entwurf;
                  return (
                    <tr
                      key={v.id}
                      onClick={() => navigate(`/app/fahrzeuge/${v.id}`)}
                      data-testid={`vehicle-row-${v.bestandsnummer}`}
                      className="border-b border-border/50 cursor-pointer hover:bg-accent/40 transition-colors"
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          {v.cover_image_url ? (
                            <img src={v.cover_image_url} alt="" className="h-11 w-16 rounded-md object-cover" loading="lazy" />
                          ) : (
                            <div className="h-11 w-16 rounded-md bg-secondary flex items-center justify-center">
                              <ImageOff className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-medium truncate">{v.marke} {v.modell}</div>
                            <div className="text-xs text-muted-foreground font-mono">{v.bestandsnummer}{v.is_demo ? " · DEMO" : ""}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-muted-foreground">{fmtKm(v.kilometerstand)}</td>
                      <td className="px-3 py-2.5 tabular-nums font-medium">{fmtEUR(v.price)}</td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground">{AVAILABILITY_META[v.verfuegbarkeit]?.label || "–"}</td>
                      <td className="px-3 py-2.5"><StatusPill label={meta.label} color={meta.color} /></td>
                      <td className="px-3 py-2.5 text-xs">
                        {v.publication_summary.published > 0 ? (
                          <span className="text-emerald-300 tabular-nums">{v.publication_summary.published} Kanal/K.</span>
                        ) : v.publication_summary.failed > 0 ? (
                          <span className="text-red-300">Fehler</span>
                        ) : (
                          <span className="text-muted-foreground">–</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground tabular-nums">{fmtDateTime(v.updated_at)}</td>
                      <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}><QuickActions v={v} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>

          {/* Mobile Cards */}
          <div className="md:hidden grid gap-3">
            {filtered.map((v) => {
              const meta = SIMPLE_STATUS_META[v.simple_status] || SIMPLE_STATUS_META.entwurf;
              return (
                <Card key={v.id} onClick={() => navigate(`/app/fahrzeuge/${v.id}`)} data-testid={`vehicle-card-${v.bestandsnummer}`} className="overflow-hidden cursor-pointer card-hover">
                  <div className="flex">
                    {v.cover_image_url ? (
                      <img src={v.cover_image_url} alt="" className="w-28 object-cover" loading="lazy" />
                    ) : (
                      <div className="w-28 bg-secondary flex items-center justify-center min-h-[96px]">
                        <ImageOff className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 p-3 min-w-0">
                      <div className="flex items-start justify-between gap-1">
                        <div className="text-sm font-semibold truncate">{v.marke} {v.modell}</div>
                        <QuickActions v={v} />
                      </div>
                      <div className="text-xs text-muted-foreground tabular-nums">{fmtKm(v.kilometerstand)} · {fmtEUR(v.price)}</div>
                      <div className="mt-1.5"><StatusPill label={meta.label} color={meta.color} /></div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Lösch-Bestätigung */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="vehicle-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Fahrzeug endgültig löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? (
                <>
                  <span className="font-medium text-foreground">{deleteTarget.marke} {deleteTarget.modell}</span>
                  {" "}(<span className="font-mono">{deleteTarget.bestandsnummer}</span>) wird mit allen Fotos, Aufgaben,
                  Notizen und der Preishistorie unwiderruflich entfernt. Diese Aktion kann nicht rückgängig gemacht werden.
                </>
              ) : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBusy} data-testid="vehicle-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleteBusy}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="vehicle-delete-confirm"
            >
              {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
